import { eventConversationWorker } from 'src/models/chat';
// Messenger 2.0 sidebar (issue #420). Renders the per-tab thread list off
// the new `useMyChats` hook. Per-user unread comes from
// `ChatParticipants.unreadCount`. Each row exposes a kebab menu with
// Archive / Unarchive, which writes per-participant state via
// `useArchiveChat`.
//
// We keep the legacy `SidebarContent.tsx` untouched so its existing search-
// user / shift-chats flow still works for the legacy single-stream view —
// the tabbed shell composes this new component in its place.

import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';
import {
  AttachmentSharp,
  Archive as ArchiveIconMui,
  Unarchive as UnarchiveIconMui,
} from '@mui/icons-material';
import { MoreVerticalIcon } from 'lucide-react';
import moment from 'moment';
import { useState } from 'react';

import { config } from 'src/config';
import Label from 'src/components/Label';
import { useArchiveChat, useEventThreads, useMyChats } from 'src/queries/chat';
import { workerName } from './eventThreadLinks';

import type { Chat, ChatParticipant, EventThread } from 'src/models/chat';

const ListItemWrapper = styled(ListItemButton)(
  ({ theme }) => `
        &.MuiButtonBase-root {
            margin: ${theme.spacing(1)} 0;
        }
  `
);

interface Messenger2SidebarProps {
  filter: { type?: string; jobId?: number; archived?: boolean };
  selectedChatId: number | undefined;
  onSelect: (chat: Chat) => void;
  currentUserId: number;
}

function pickMyParticipant(
  chat: Chat,
  currentUserId: number
): ChatParticipant | undefined {
  if (!chat.participants) return undefined;
  // The controller scopes the `participants` include to the caller's row,
  // so participants[0] is usually it. Fall back to a filter for safety.
  return (
    chat.participants.find((p) => p.userId === currentUserId) ??
    chat.participants[0]
  );
}

/**
 * Resolve the "other" participant for a DIRECT/JOB_DIRECT thread. The
 * `GET /chats/me` payload now returns *all* participants (including the
 * caller), so we must exclude the caller's own row by `userId` before
 * picking a name/avatar — otherwise a self-DM-shaped payload (or simply the
 * caller appearing first) would surface the caller's own name. Falls back to
 * the first participant that carries a hydrated `user` snapshot when the
 * caller can't be identified (e.g. older cached payloads without `userId`).
 */
function findOtherParticipant(
  chat: Chat,
  currentUserId: number
): ChatParticipant | undefined {
  if (!chat.participants) return undefined;
  return (
    chat.participants.find(
      (p) => p.user?.id && p.userId !== currentUserId
    ) ?? chat.participants.find((p) => p.user?.id)
  );
}

function describeThread(chat: Chat, currentUserId: number): string {
  const worker = eventConversationWorker(chat);
  const workerName = worker && [worker.firstName, worker.lastName].filter(Boolean).join(' ');
  if (chat.groupName) return workerName ? `${workerName} · ${chat.groupName}` : chat.groupName;
  if (chat.supportSubject) return chat.supportSubject;
  if (chat.type === 'JOB_GROUP') {
    return `Job ${chat.jobId ?? ''} group`.trim();
  }
  if (chat.type === 'JOB_BROADCAST') {
    return chat.lastMessage ? 'Broadcast' : 'Job broadcast';
  }
  // DIRECT / JOB_DIRECT — surface the other participant if hydrated.
  const other = findOtherParticipant(chat, currentUserId);
  if (other?.user) {
    const f = other.user.firstName ?? '';
    const l = other.user.lastName ?? '';
    return `${f} ${l}`.trim() || 'Direct message';
  }
  return 'Conversation';
}

function describeAvatarSrc(
  chat: Chat,
  currentUserId: number
): string | undefined {
  const other = findOtherParticipant(chat, currentUserId);
  if (!other?.user?.profileImage) return undefined;
  return (config.FILe_URL ?? '') + other.user.profileImage;
}

export default function Messenger2Sidebar({
  filter,
  selectedChatId,
  onSelect,
  currentUserId,
}: Messenger2SidebarProps) {
  const { data: chats = [], isFetching } = useMyChats(filter);
  const archiveMutation = useArchiveChat();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuChat, setMenuChat] = useState<Chat | null>(null);

  const handleOpenMenu = (event: React.MouseEvent, chat: Chat) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
    setMenuChat(chat);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuChat(null);
  };

  const handleArchiveToggle = () => {
    if (!menuChat) return;
    const myParticipant = pickMyParticipant(menuChat, currentUserId);
    const archived = !(myParticipant?.archivedAt || menuChat.isArchived);
    archiveMutation.mutate(
      { chatId: menuChat.id, archived },
      { onSettled: handleCloseMenu }
    );
  };

  if (isFetching && chats.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (chats.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary">
          No conversations to show.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {chats.map((chat) => {
        const myParticipant = pickMyParticipant(chat, currentUserId);
        const unread = myParticipant?.unreadCount ?? 0;
        const archived = !!(myParticipant?.archivedAt || chat.isArchived);
        const isOwnLastSender = chat.lastSenderId === currentUserId;
        return (
          <ListItemWrapper
            key={chat.id}
            selected={chat.id === selectedChatId}
            onClick={() => onSelect(chat)}
            data-testid={`thread-row-${chat.id}`}
          >
            <ListItemAvatar>
              <Avatar src={describeAvatarSrc(chat, currentUserId)} />
            </ListItemAvatar>
            <ListItemText
              sx={{ mr: 1 }}
              primaryTypographyProps={{
                color: 'textPrimary',
                variant: 'h5',
                noWrap: true,
              }}
              secondaryTypographyProps={{
                color: 'textSecondary',
                noWrap: true,
              }}
              primary={describeThread(chat, currentUserId)}
              secondary={
                chat.lastMessage ? (
                  chat.lastMessageType === 'text' ? (
                    chat.lastMessage
                  ) : (
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <AttachmentSharp fontSize="small" />
                      &nbsp;Media
                    </Box>
                  )
                ) : (
                  'Start conversation'
                )
              }
            />
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography variant="caption" color="text.secondary">
                {chat.updatedAt ? moment(chat.updatedAt).fromNow() : ''}
              </Typography>
              {unread > 0 && !isOwnLastSender && (
                <Label color="primary">
                  <b>{unread}</b>
                </Label>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={(e) => handleOpenMenu(e, chat)}
              aria-label={`Actions for chat ${chat.id}`}
              data-testid={`thread-menu-${chat.id}`}
            >
              <MoreVerticalIcon fontSize="small" />
            </IconButton>
            {archived && (
              <Box sx={{ ml: 1 }}>
                <Label color="secondary">
                  <small>Archived</small>
                </Label>
              </Box>
            )}
          </ListItemWrapper>
        );
      })}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={handleArchiveToggle}
          disabled={archiveMutation.isLoading}
          data-testid="thread-archive-toggle"
        >
          <ListItemIcon>
            {menuChat &&
            (pickMyParticipant(menuChat, currentUserId)?.archivedAt ||
              menuChat.isArchived) ? (
              <UnarchiveIconMui fontSize="small" />
            ) : (
              <ArchiveIconMui fontSize="small" />
            )}
          </ListItemIcon>
          {menuChat &&
          (pickMyParticipant(menuChat, currentUserId)?.archivedAt ||
            menuChat.isArchived)
            ? 'Unarchive chat'
            : 'Archive chat'}
        </MenuItem>
      </Menu>
    </Box>
  );
}

/**
 * Messages hub (#4005): worker-event threads grouped by event, then by worker
 * within each event. Reuses the thread row anatomy above.
 */
export function EventThreadsSidebar({
  selectedChatId,
  onSelect,
  currentUserId,
}: {
  selectedChatId: number | undefined;
  onSelect: (thread: EventThread) => void;
  currentUserId: number;
}) {
  const { data: threads = [], isFetching, isError } = useEventThreads();

  if (isFetching && threads.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="error">
          Event threads could not load. Refresh the page or sign in again.
        </Typography>
      </Box>
    );
  }
  if (threads.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary">
          No event threads yet. Start one from an event's Bookings.
        </Typography>
      </Box>
    );
  }

  const groups: Array<{ eventId: number; title: string; threads: EventThread[] }> = [];
  for (const thread of threads) {
    const eventId = thread.Event?.id ?? thread.eventId ?? 0;
    let group = groups.find(item => item.eventId === eventId);
    if (!group) {
      group = { eventId, title: thread.Event?.title || `Event ${eventId}`, threads: [] };
      groups.push(group);
    }
    group.threads.push(thread);
  }

  return (
    <Box data-testid="event-threads-sidebar">
      {groups.map(group => (
        <Box key={group.eventId} component="section" aria-label={group.title} mb={1}>
          <Typography variant="h5" color="text.secondary" sx={{ pt: 1 }} data-testid={`event-group-${group.eventId}`}>
            {group.title}
          </Typography>
          {group.threads.map(thread => {
            const isOwnLastSender = thread.lastSenderId === currentUserId;
            return (
              <ListItemWrapper
                key={thread.id}
                selected={thread.id === selectedChatId}
                onClick={() => onSelect(thread)}
                data-testid={`event-thread-row-${thread.id}`}
              >
                <ListItemAvatar>
                  <Avatar src={thread.worker?.profileImage ? `${config.FILe_URL ?? ''}${thread.worker.profileImage}` : undefined} />
                </ListItemAvatar>
                <ListItemText
                  sx={{ mr: 1 }}
                  primaryTypographyProps={{ color: 'textPrimary', variant: 'h5', noWrap: true }}
                  secondaryTypographyProps={{ color: 'textSecondary', noWrap: true }}
                  primary={workerName(thread.worker)}
                  secondary={thread.lastMessage || 'Start conversation'}
                />
                <Box display="flex" flexDirection="column" alignItems="flex-end" flexShrink={0}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {thread.updatedAt ? moment(thread.updatedAt).fromNow(true) : ''}
                  </Typography>
                  {thread.unreadCount > 0 && !isOwnLastSender && (
                    <Label color="primary">
                      <b>{thread.unreadCount}</b>
                    </Label>
                  )}
                </Box>
              </ListItemWrapper>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
